/**
 * RelatedFoodItemsStrip — used on article detail pages to surface food
 * items mentioned in the article. Renders nothing when no matches exist.
 *
 * Reads from /api/cross-links/article/{slug}/food-items.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { UtensilsCrossed, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function RelatedFoodItemsStrip({ articleSlug }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!articleSlug) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/api/cross-links/article/${encodeURIComponent(articleSlug)}/food-items`,
          { params: { limit: 6 } },
        );
        if (!cancelled) setItems(r.data?.items || []);
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [articleSlug]);

  if (loading || items.length === 0) return null;

  return (
    <section
      className="mt-14 mb-4 rounded-2xl border p-5 sm:p-6"
      style={{ background: "var(--wofy-surface, #FAF7F2)", borderColor: "var(--wofy-line, #E8DFCF)" }}
      data-testid="related-food-items-strip"
    >
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2">
          <UtensilsCrossed className="w-4 h-4" style={{ color: "var(--wofy-primary, #EA580C)" }} />
          <div className="text-[10px] font-mono uppercase tracking-widest"
               style={{ color: "var(--wofy-primary, #EA580C)" }}>
            Related food items
          </div>
        </div>
        <Link
          to="/food-pulse/food"
          className="text-[11px] font-mono uppercase tracking-widest hover:underline inline-flex items-center gap-1"
          style={{ color: "var(--wofy-primary, #EA580C)" }}
          data-testid="related-food-items-see-all"
        >
          Food encyclopedia <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((f) => (
          <Link
            key={f.slug}
            to={`/food-pulse/food/${f.slug}`}
            className="group flex gap-3 p-2 rounded-lg hover:shadow-md transition-shadow bg-white"
            style={{ border: "1px solid var(--wofy-line, #E8DFCF)" }}
            data-testid={`related-food-item-${f.slug}`}
          >
            {f.image ? (
              <img
                src={f.image}
                alt=""
                className="w-16 h-16 rounded-md object-cover flex-none"
                loading="lazy"
              />
            ) : (
              <div className="w-16 h-16 rounded-md flex-none flex items-center justify-center"
                   style={{ background: "var(--wofy-surface, #FAF7F2)" }}>
                <UtensilsCrossed className="w-5 h-5 opacity-40"
                                 style={{ color: "var(--wofy-primary, #EA580C)" }} />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium line-clamp-2 group-hover:underline"
                   style={{ color: "var(--wofy-text, #1F1512)" }}>
                {f.common_name}
              </div>
              {f.description && (
                <div className="text-[11px] mt-1 line-clamp-2"
                     style={{ color: "var(--wofy-text-soft, #6B4A2E)" }}>
                  {f.description}
                </div>
              )}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
